import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

export const MultiSelectCheckbox = ({ 
  options, 
  selected = [], 
  onChange, 
  label,
  showOther = true,
  otherPlaceholder = "Specify other...",
  columns = 3,
  className = ""
}) => {
  const [otherValue, setOtherValue] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);
  
  // Ensure selected is always an array
  const selectedArray = Array.isArray(selected) ? selected : [];
  
  // Separate standard options from custom "other" values
  const standardOptions = new Set(options);
  const customValues = selectedArray.filter(item => !standardOptions.has(item));
  
  const handleToggle = (option) => {
    const newSelected = selectedArray.includes(option)
      ? selectedArray.filter(item => item !== option)
      : [...selectedArray, option];
    onChange(newSelected);
  };
  
  const handleOtherToggle = () => {
    setShowOtherInput(!showOtherInput);
    if (showOtherInput) {
      setOtherValue("");
    }
  };
  
  const handleAddOther = (e) => {
    if (e.key === "Enter" && otherValue.trim()) {
      if (!selectedArray.includes(otherValue.trim())) {
        onChange([...selectedArray, otherValue.trim()]);
      }
      setOtherValue("");
    }
  };
  
  const handleRemoveCustom = (value) => {
    onChange(selectedArray.filter(item => item !== value));
  };
  
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && <Label className="text-zinc-300">{label}</Label>}
      
      <div className={`grid ${gridCols[columns] || gridCols[3]} gap-3`}>
        {options.map(option => (
          <div key={option} className="flex items-center space-x-2">
            <Checkbox
              id={`multi-${option}`}
              checked={selectedArray.includes(option)}
              onCheckedChange={() => handleToggle(option)}
              className="border-white/20 data-[state=checked]:bg-[#00f0ff] data-[state=checked]:border-[#00f0ff]"
            />
            <Label 
              htmlFor={`multi-${option}`} 
              className="text-zinc-400 text-sm cursor-pointer hover:text-zinc-200 transition-colors"
            >
              {option}
            </Label>
          </div>
        ))}
        
        {showOther && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="multi-other"
              checked={showOtherInput || customValues.length > 0}
              onCheckedChange={handleOtherToggle}
              className="border-white/20 data-[state=checked]:bg-[#7000ff] data-[state=checked]:border-[#7000ff]"
            />
            <Label 
              htmlFor="multi-other" 
              className="text-zinc-400 text-sm cursor-pointer hover:text-zinc-200 transition-colors"
            >
              Other
            </Label>
          </div>
        )}
      </div>
      
      {/* Custom values display */}
      {customValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {customValues.map(value => (
            <Badge 
              key={value} 
              variant="secondary" 
              className="bg-[#7000ff]/20 text-[#7000ff] border border-[#7000ff]/30 flex items-center gap-1 px-2 py-1"
            >
              {value}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-white" 
                onClick={() => handleRemoveCustom(value)}
              />
            </Badge>
          ))}
        </div>
      )}
      
      {/* Other input field */}
      {(showOtherInput || customValues.length > 0) && showOther && (
        <Input
          placeholder={otherPlaceholder}
          value={otherValue}
          onChange={(e) => setOtherValue(e.target.value)}
          onKeyDown={handleAddOther}
          className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500 mt-2 max-w-xs"
        />
      )}
    </div>
  );
};
